import { Force_PackageListing } from '@oneappexchange/oaf-schema';
import { ListingResponse } from '~/modules/services/listing_service';
import { selectExtensionListing } from '~/modules/utilities/listing_utils';
import { BadgeKey } from './badgeHelper';

type SupportedFeature =
    | (keyof I18nMessages['listingDetails']['badges'] & keyof I18nMessages['listingDetails']['tooltipsText'])
    | undefined;

type SupportedFeatureCheckProps = {
    listing: ListingResponse;
    extension?: schema.oaf.Force_Listing;
};

type SupportedFeatureCheck = ({ listing, extension }: SupportedFeatureCheckProps) => string;

const SupportedFeatureChecks: SupportedFeatureCheck[] = [
    // @ts-expect-error * Automated Type Error Fix
    getNativeApp,
    // @ts-expect-error * Automated Type Error Fix
    makeGetIsManaged('hasNoLimits'),
    getSimpleFeature('isLightningReady'),
    getSimpleFeature('isEinsteinAgent'),
    // @ts-expect-error * Automated Type Error Fix
    getIsEditableCode,
    getSimpleFeature('isMultiCurrency'),
    getSimpleFeature('isPersonAccounts'),
    getSimpleFeature('isFedrampCertified'),
    getSimpleFeature('isSalesforceShield'),
    getSimpleFeature('isSalesProgram'),
    // @ts-expect-error * Automated Type Error Fix
    getIsCommunityBuilder,
    // @ts-expect-error * Automated Type Error Fix
    makeGetIsManaged('isManaged'),
    getSimpleFeature('isLightningComponent'),
    getSimpleFeature('isSalesforce1Mobile'),
    getSimpleFeature('isAccessibleSolution'),
];

/**
 * @param listing
 * @returns array with supported features as badge keys to map from localization file based on direct oAF field mappings
 */
export function selectSupportFeatures(listing: ListingResponse): BadgeKey[] {
    const extension = selectExtensionListing(listing);
    const supportedFeatures: BadgeKey[] = [];
    for (const checkFunction of SupportedFeatureChecks) {
        const realValue = checkFunction({ listing, extension });
        if (realValue) {
            supportedFeatures.push(realValue as BadgeKey);
        }
    }

    return supportedFeatures;
}

/**
 * Returns a function that does a field check on the listing extension object,
 * and returns the field (if it's true), or undefined.
 */
function getSimpleFeature(field: SupportedFeature) {
    // @ts-expect-error * Automated Type Error Fix
    const checkExtensionField: SupportedFeatureCheck = ({ extension }) => {
        // @ts-expect-error * Automated Type Error Fix
        return extension?.[field] ? field : undefined;
    };
    return checkExtensionField;
}

/**
 * This is a higher order function that allows us to return a check function that returns
 * different "supported features" based on whether a package is managed or not,
 * Component types need to return "isManaged", other types need to return "hasNoLimits".
 */
function makeGetIsManaged(
    supportedFeature: 'hasNoLimits' | 'isManaged',
): (props: SupportedFeatureCheckProps) => SupportedFeature {
    return ({ listing }: SupportedFeatureCheckProps): SupportedFeature => {
        const { isForcePackageTechnology, isManaged } = checkIsManaged(listing);
        return isForcePackageTechnology && isManaged ? supportedFeature : undefined;
    };
}

// Logic mirrored from 'getIsNative' function in: https://git.soma.salesforce.com/appexchange/tz-ecosystem/blob/develop/app/src/classes/AppxListingDetailOverviewTabController.cls
function getNativeApp({ extension }: SupportedFeatureCheckProps): SupportedFeature {
    // Logic mirrored from 'getIsLightningComponent' function in: https://git.soma.salesforce.com/appexchange/tz-ecosystem/blob/develop/app/app/src/classes/Listing.cls
    const isComponentListing = extension?.recordType === 'Component';
    const featured = extension?.featured;
    return !isComponentListing && featured ? 'isNativeApp' : undefined;
}

function getIsEditableCode({ listing, extension }: SupportedFeatureCheckProps): SupportedFeature {
    const PACKAGE_LISTINNG_EXTENSION_TYPE = 'listing/extensions/force/listings/Listing/extensions/PackageListing';
    const { isForcePackageTechnology, isManaged } = checkIsManaged(listing);

    const gitHubRepository = (
        extension?.extensions?.find(({ extensionType }) => extensionType === PACKAGE_LISTINNG_EXTENSION_TYPE)
            ?.data as Force_PackageListing
    )?.gitHubRepositoryURL;

    if ((isForcePackageTechnology && !isManaged) || gitHubRepository) {
        return 'isEditableCode';
    }
    return undefined;
}

function getIsCommunityBuilder({ listing }: SupportedFeatureCheckProps): SupportedFeature {
    const solution = listing?.solution;
    if (!solution) {
        return undefined;
    }
    const isCommunityBuilder = (listing.solution?.solution as schema.oaf.Force_Solution_Package)?.manifest
        ?.isCommunityBuilder;
    return isCommunityBuilder ? 'isCommunityBuilder' : undefined;
}

/**
 * Checks if a given listing response is managed by a Salesforce package.
 *
 * @param listing The listing response to check.
 * @returns Returns true if the listing is managed by a Salesforce package, false otherwise.
 */
function checkIsManaged(listing: ListingResponse): {
    isForcePackageTechnology: boolean;
    isManaged: boolean;
} {
    const isForcePackageTechnology = listing.solution?.technology === 'force/Package';
    const isManaged = (listing.solution?.solution as schema.oaf.Force_Solution_Package)?.manifest?.isManaged ?? false;
    return {
        isForcePackageTechnology,
        isManaged,
    };
}
